import { type LucideIcon } from 'lucide-react';

import { FormControl, FormItem, FormLabel, FormMessage } from './form';
import { IconFieldFrame, iconFieldInputClass } from './IconField';
import { Input } from './input';
import { PasswordInput } from './password-input';

/**
 * A labelled form field with a leading icon, for use inside a react-hook-form
 * `FormField` render prop.
 *
 * **`autoComplete` is a required prop, not an optional one.** The form this was
 * extracted from set neither `autoComplete` nor `inputMode` on any field, so
 * password managers and mobile autofill did not work on its sign-in screen at
 * all. Required means a new field cannot quietly ship without one.
 *
 * `FormControl` wraps the INPUT directly, never the positioning frame. It is a
 * Radix `Slot`, so it merges `id` and `aria-describedby` onto whatever element
 * it is given - and it was once given the wrapper `<div>`, which meant
 * `FormLabel`'s `htmlFor` resolved to a div and the input had no associated
 * label. That went unnoticed because every field carried a placeholder, and a
 * placeholder is accepted as a last-resort accessible name; remove the
 * placeholder and axe fails immediately.
 *
 * `type='password'` routes to `PasswordInput`, so the reveal toggle comes for
 * free - which is what lets a sign-up form drop its confirm-password field.
 */
export function IconFormField({
  icon,
  label,
  placeholder,
  type = 'text',
  autoComplete,
  inputMode,
  field,
}: {
  icon: LucideIcon;
  label: string;
  placeholder?: string;
  type?: string;
  autoComplete: string;
  inputMode?: 'email' | 'text';
  /** The `field` object from `FormField`'s render prop. */
  field: Record<string, unknown>;
}) {
  const isPassword = type === 'password';

  return (
    // No spacing override — `FormItem`'s default IS this field rhythm now.
    <FormItem>
      <FormLabel className='text-label text-foreground'>{label}</FormLabel>
      <IconFieldFrame icon={icon}>
        <FormControl>
          {isPassword ? (
            <PasswordInput
              className={iconFieldInputClass}
              placeholder={placeholder}
              autoComplete={autoComplete}
              {...field}
            />
          ) : (
            <Input
              type={type}
              inputMode={inputMode}
              autoComplete={autoComplete}
              placeholder={placeholder}
              className={iconFieldInputClass}
              {...field}
            />
          )}
        </FormControl>
      </IconFieldFrame>
      <FormMessage />
    </FormItem>
  );
}
